import {QueryClient, useQuery} from '@tanstack/react-query';
import {useSelector} from 'react-redux';
import {fetchRest} from '../helpers/fetchRest';
import utils from '../helpers/utils';
import {privacyMonitorUserStatusCode} from '../constants/errorCodeMap';
import {RootState} from '../reducers/rootReducer';

export const QUERY_KEY_PRIVACY_MONITOR_SUMMARY = 'privacyMonitor';
export const QUERY_KEY_PRIVACY_MONITOR_LICENSE = 'privacyMonitorLicense';

const isPrivacyMonitorCountry = (countryCode: string): boolean => countryCode === 'US' || countryCode === 'CA';

export type PrivacyMonitorLicenseState = {
  validateLicense: boolean;
  validLicense: boolean;
} | null;

/** True when the member has a validated Privacy Monitor license. Delegates to utils' canonical check. */
export const isPrivacyMonitorLicenseValid = (license?: PrivacyMonitorLicenseState): boolean =>
  utils.hasValidPrivacyMonitorLicense(license);

export const getPrivacyMonitorQueryKeys = (countryCode: string, accountId = '') => ({
  summary: [QUERY_KEY_PRIVACY_MONITOR_SUMMARY, countryCode, accountId] as const,
  license: [QUERY_KEY_PRIVACY_MONITOR_LICENSE, countryCode, accountId] as const,
});

/**
 * Invalidates cached Privacy Monitor summary and license queries after auth is established.
 * Active observers refetch automatically; partner iframe sessions keep React Query errors
 * indefinitely unless invalidated.
 */
export const invalidatePrivacyMonitorQueries = async(queryClient: QueryClient, countryCode: string): Promise<void> => {
  if (!isPrivacyMonitorCountry(countryCode)) {
    return;
  }

  try {
    await Promise.all([
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_PRIVACY_MONITOR_SUMMARY, countryCode]}),
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_PRIVACY_MONITOR_LICENSE, countryCode]}),
    ]);
  } catch (error) {
    utils.log({
      error,
      message: 'Failed to invalidate Privacy Monitor queries',
    });
  }
};

/**
 *  Fetch user privacy monitor summary results through member-api.
 * @param {string} countryCode  - user's country code.
 * @return {Object} privacy monitor summary data, various response status(s),
 * and customize object for handling dashboard privacy monitor widget logics.
 */
export const usePrivacyMonitorSummaryResults = (countryCode: string) => {
  const url = utils.getLPMPrivacyResultsSummaryPath();
  const accountId = useSelector((store: RootState) => utils.safeGet(store.auth, 'user.primaryMember.accountId', ''));
  const enabled = isPrivacyMonitorCountry(countryCode) && Boolean(accountId);

  const {isError, error, isPending, isSuccess, data, refetch} = useQuery({
    queryKey: getPrivacyMonitorQueryKeys(countryCode, accountId).summary,
    queryFn: () =>
      fetchRest(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'include',
      }),
    enabled,
    retry: 1,
  });

  if (!enabled) {
    // Blocked on auth (no accountId yet), not an actual error — consumers should check isBlocked, not isError.
    return {
      data: null,
      refetch: () => {},
      isLoading: false,
      isSuccess: false,
      isError: false,
      isBlocked: true,
      error: null,
    };
  }

  const topThreeResults = (data as any)?.topThreeResults;
  let topTwoResults: any[] = [];
  if (topThreeResults && topThreeResults.length > 0) {
    topTwoResults = topThreeResults.length === 3 ? topThreeResults.slice(0, -1) : [...topThreeResults];
  }

  const privacyMonitor = {
    ...(data as any),
    topTwoResults,
    isNewUser: (error as unknown as PrivacyError)?.errorCode === privacyMonitorUserStatusCode || false,
  };

  return {isError, error, isLoading: isPending, isSuccess, isBlocked: false, data: privacyMonitor, refetch};
};

/**
 *  Fetch user license information for Privacy Monitor from colp through member-api.
 *  License info will be used to determine entitlement status of the user to provide member experience on Dashboard.
 * @param {string} countryCode  - user's country code.
 * @return {Object} license data, and various response status(s)
 */
export const usePrivacyMonitorLicense = (countryCode: string) => {
  const url = utils.getValidateLicensePath();
  const accountId = useSelector((store: RootState) => utils.safeGet(store.auth, 'user.primaryMember.accountId', ''));
  const enabled = isPrivacyMonitorCountry(countryCode) && Boolean(accountId);

  const query = useQuery({
    queryKey: getPrivacyMonitorQueryKeys(countryCode, accountId).license,
    queryFn: () => fetchRest(url),
    enabled,
    retry: 1,
  });

  if (!enabled) {
    // Blocked on auth (no accountId yet), not an actual error — consumers should check isBlocked, not isError.
    return {
      data: {validateLicense: false, validLicense: false},
      refetch: () => {},
      isLoading: false,
      isError: false,
      isBlocked: true,
      isSuccess: false,
      error: null,
    };
  }

  return {...query, isLoading: query.isPending, isBlocked: false};
};
